<template>
  <div class="ps-card">
    <div class="ps-card__head">
      <div>
        <div class="ps-card__total">
          {{ $numberFormat(sumOfSells) }}
          {{ $store.state.currency.name }}
        </div>
        <div class="ps-card__title">{{ $t("sale-by-product") }}</div>
      </div>
      <div class="ps-card__tools">
        <v-select
          v-model="selectedCount"
          :items="countItems"
          :label="$t('productCount')"
          dense
          outlined
          hide-details
          item-text="name"
          item-value="value"
          append-icon="mdi-chevron-down"
          class="ps-card__select"
          @change="fetchList"
        />
      </div>
    </div>
    <div v-if="canSee" class="ps-card__body">
      <apexchart
        v-if="hasData"
        :key="chartKey"
        type="bar"
        :height="chartHeight"
        :options="chartOptions"
        :series="series"
      />
      <div v-else class="ps-card__empty">
        <v-progress-circular
          v-if="loading"
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
        <home-empty-state v-else />
      </div>
    </div>
    <div v-else class="ps-card__empty">
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from "./HomeEmptyState.vue";

export default {
  name: "HomeProductSales",
  components: { HomeEmptyState },
  props: {
    dateType: {
      type: String,
      default: "7days",
    },
  },
  data() {
    return {
      products: [],
      loading: false,
      selectedCount: 5,
    };
  },
  computed: {
    canSee() {
      return (
        this.$store.getters.getpermission &&
        this.$store.getters.getpermission.includes("overview")
      );
    },
    countItems() {
      return [
        { name: this.$t("top-5-product"), value: 5 },
        { name: this.$t("top-10-product"), value: 10 },
        { name: this.$t("top-15-product"), value: 15 },
      ];
    },
    hasData() {
      return this.products.length > 0;
    },
    chartKey() {
      return this.dateType + "-" + this.selectedCount + "-" + this.products.length;
    },
    chartHeight() {
      return 280;
    },
    sumOfSells() {
      return this.products.reduce((sum, item) => sum + this.productAmount(item), 0);
    },
    series() {
      return [
        {
          name: this.$t("sell"),
          data: this.products.map((item) => this.productAmount(item)),
        },
      ];
    },
    chartOptions() {
      const self = this;
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "IRANSans",
          width: "100%",
          background: "transparent",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: "60%",
            borderRadius: 6,
          },
        },
        colors: ["#0066FF"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.products.map((item) => this.shortName(item.name)),
          labels: {
            style: { fontSize: "10px" },
            formatter(val) {
              return self.$numberFormat(val);
            },
          },
        },
        yaxis: {
          labels: {
            style: { fontSize: "11px" },
            offsetX: this.$vuetify.rtl ? -70 : 0,
          },
        },
        grid: {
          borderColor: "#EEEEEE",
          strokeDashArray: 4,
        },
        tooltip: {
          rtl: true,
          x: {
            formatter(val, opts) {
              const item = self.products[opts.dataPointIndex];
              return item && item.name ? item.name : val;
            },
          },
          y: {
            formatter(val) {
              return (
                self.$numberFormat(val) + " " + (self.$store.state.currency.name || "")
              );
            },
          },
        },
        legend: { show: false },
      };
    },
  },
  watch: {
    dateType() {
      this.fetchList();
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    productName(item) {
      if (item.product && item.product.name) return item.product.name;
      return item.name || item.title || "—";
    },
    shortName(name) {
      return String(name || "")
        .split(" ")
        .slice(0, 2)
        .join(" ");
    },
    productAmount(item) {
      return parseFloat(item.amount_invoice || item.sell || item.amount || 0) || 0;
    },
    fetchList() {
      this.loading = true;
      this.$axios
        .get(this.$endpoint + "invoiceReport", {
          params: {
            select: "typeProductItems",
            type: "sell",
            page: 1,
            dateType: this.dateType,
            options: JSON.stringify({
              page: 1,
              itemsPerPage: this.selectedCount,
            }),
          },
        })
        .then((res) => {
          const rows = (res.data && res.data.data) || [];
          this.products = rows
            .map((item) => ({
              ...item,
              name: this.productName(item),
            }))
            .sort((a, b) => this.productAmount(b) - this.productAmount(a));
        })
        .catch(() => {
          this.products = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.ps-card {
  background: #f8fbff;
  border: 1px solid #e8eef7;
  border-radius: 4px;
  padding: 12px 14px 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ps-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.ps-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
  margin-top: 2px;
}
.ps-card__total {
  font-size: 13px;
  font-weight: 700;
  color: #111;
  white-space: nowrap;
}
.ps-card__tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.ps-card__select {
  width: 148px;
  font-size: 12px;
}
.ps-card__body {
  min-height: 0;
  flex: 1;
}
.ps-card__empty {
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}
::v-deep .ps-card__select.v-text-field--outlined.v-input--dense .v-input__slot {
  min-height: 32px !important;
}
::v-deep .ps-card__select .v-label {
  font-size: 11px !important;
}
</style>
